/**
 * Presentational view for the all-pipelines drawer (SE-5489) — the
 * recruitment-pipeline sibling of the Scouting `SeeAllListsDrawerView`.
 *
 * Renders the right-anchored Drawer atom with a title header, a search input,
 * and three collapsible grouped sections (Personal / Team / System). Pure
 * presentation — every piece of state and every handler is owned by the
 * Container. When no pipelines match, a single empty state replaces the
 * sections so the drawer never renders three empty headers or a broken layout.
 * @module features/player-kanban/components/molecules/SeeAllPipelinesDrawer/SeeAllPipelinesDrawerView
 */

import { ChevronDown, Plus, SearchIcon, X } from "lucide-react-native";
import React, { memo } from "react";

import SeeAllPipelinesSection from "./SeeAllPipelinesSection";

import type { PipelineDrawerRow } from "@/features/player-kanban/hooks/useSeeAllPipelinesData";

import {
  Drawer,
  DrawerBackdrop,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
} from "@/components/atoms/Drawer";
import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Icon } from "@/components/atoms/Icon";
import SelectDropdown, {
  type SelectDropdownOption,
} from "@/components/molecules/SelectDropdown";
import { Stack } from "@/components/atoms/Stack";
import { Input } from "@/components/atoms/Input";
import { Spinner } from "@/components/atoms/Spinner";
import { Text } from "@/components/atoms/Text";

import { TEST_ID_SEE_ALL_PIPELINES_DRAWER } from "e2e/testId";

/**
 * Sort-trigger chrome. Same 36-tall, radius-4, neutral-outline family as the
 * search field beside it, so the controls block reads as one row of siblings —
 * the pattern the Add Players drawer's control row established.
 *
 * `self-start` rather than `w-full` (Raniel, 2026-08-06): the select HUGS its
 * label. The Figma snippet's `width: "100%"` is the isolated artboard wrapper,
 * not the drawer context — stretched full-bleed it read as a banner across the
 * list instead of a compact control on its own row. The shell Root still spans
 * the column, so the trigger has to opt out of the default stretch itself.
 *
 * Everything else already matched the spec: h-9 (36), `bg-surface-base` white,
 * 1px `outline-strong` (= #d3d3d3), px-3 (the 11px spec value on the 4px grid),
 * gap-2 (8), and a `body` label — `content-primary` resolves to #262627 in
 * light, exactly the spec's colour.
 */
const SORT_TRIGGER_CLASS =
  "web:hover:bg-surface-subtle h-9 flex-row items-center gap-2 self-start rounded-sm border border-outline-strong bg-surface-base px-3";

/**
 * Trigger content for the row-ordering select.
 * @param label - The active ordering, spelled out.
 * @returns The trigger content.
 */
const renderSortTrigger = (label: string): React.ReactElement => (
  <>
    <Text variant="body" className="text-content-primary">
      {label}
    </Text>
    <Icon as={ChevronDown} size="sm" className="text-content-muted" />
  </>
);

/** Props for SeeAllPipelinesDrawerView. */
export interface SeeAllPipelinesDrawerViewProps {
  readonly isOpen: boolean;
  readonly loading: boolean;
  /** True when the board query failed — render the error state, not "No pipelines found". */
  readonly error: boolean;
  /** Retry handler for the error state (refetches the board set from page one). */
  readonly onRetry: () => void;
  readonly personal: readonly PipelineDrawerRow[];
  readonly team: readonly PipelineDrawerRow[];
  readonly system: readonly PipelineDrawerRow[];
  readonly isPersonalCollapsed: boolean;
  readonly isTeamCollapsed: boolean;
  readonly isSystemCollapsed: boolean;
  /** True when no pipelines match — render the empty state instead of sections. */
  readonly isEmpty: boolean;
  /**
   * SE-6775: whether pipelines exist that the switcher could not load (the
   * board-list page walk stopped while the connection still reported more
   * pages). When true the drawer states that its sections are a lower bound,
   * rather than presenting a capped list as every pipeline the user has.
   *
   * Optional and defaulting to false so an unmigrated direct consumer renders
   * the drawer exactly as before — the same shape SE-6597 used on the chip row.
   */
  readonly hasUnreachableBoards?: boolean;
  /** Current search query value (controlled by Container). */
  readonly searchQuery: string;
  /** Search input change handler. */
  readonly onSearchChange: (next: string) => void;
  /** The active row ordering, spelled out for the trigger. */
  readonly sortLabel: string;
  /** Ordering menu rows. */
  readonly sortOptions: readonly SelectDropdownOption[];
  /** Whether the ordering menu is open. */
  readonly isSortOpen: boolean;
  /** Toggles the ordering menu. */
  readonly onSortToggle: () => void;
  /** Closes the ordering menu. */
  readonly onSortClose: () => void;
  /**
   * Close the drawer and open the New Pipeline modal. Absent when the caller
   * owns no modal — the New button is then omitted rather than inert.
   */
  readonly onCreatePipeline?: () => void;
  readonly onTogglePersonal: () => void;
  readonly onToggleTeam: () => void;
  readonly onToggleSystem: () => void;
  readonly onSelectPipeline: (boardId: string) => void;
  readonly onClose: () => void;
}

/**
 * SE-6775: renders the truncation affordance — the visible counterpart of the
 * page cap that reached this drawer only as a flag nobody read.
 *
 * Deliberately the same KIND of affordance as the two surfaces already fixed,
 * combining the discipline each of them established. SE-6575's `BoardTabs`
 * shows a count and so annotates it as a lower bound (`N+ boards (more not
 * loaded)`); this drawer also shows counts — one per section — so per that
 * precedent they must be qualified. But the walk truncates GLOBALLY: nothing
 * says which section came up short, so qualifying any single section's number
 * would misattribute, and inventing a total would be worse. So the copy
 * qualifies the counts collectively and states the fact without a number,
 * which is exactly SE-6597's `PipelineChipsRow` discipline. Two precedents
 * honored, no third pattern.
 *
 * A render helper (not a component) so the populated and empty branches emit an
 * identical element — the same shape SE-6597 used.
 * @returns The affordance element.
 */
const renderTruncationIndicator = () => (
  <Box className="px-6 pt-4">
    <Text
      variant="caption"
      testID={TEST_ID_SEE_ALL_PIPELINES_DRAWER.TRUNCATION_INDICATOR}
      className="text-content-muted"
    >
      More pipelines not loaded — section counts are a lower bound
    </Text>
  </Box>
);

/**
 * All-pipelines drawer view. Right-anchored Drawer with sticky header, search
 * input, scroll body, and three grouped sections.
 * @param root0 - View props (see {@link SeeAllPipelinesDrawerViewProps})
 * @param root0.isOpen - Drawer open state
 * @param root0.loading - Apollo loading state for the drawer's board query
 * @param root0.error - Whether the board query failed (render the error state)
 * @param root0.onRetry - Retry handler for the error state
 * @param root0.personal - Rows for the Personal section
 * @param root0.team - Rows for the Team section
 * @param root0.system - Rows for the System section
 * @param root0.isPersonalCollapsed - Collapsed state for Personal
 * @param root0.isTeamCollapsed - Collapsed state for Team
 * @param root0.isSystemCollapsed - Collapsed state for System
 * @param root0.isEmpty - Whether no pipelines match (render empty state)
 * @param root0.hasUnreachableBoards - Whether pipelines exist beyond those loaded (SE-6775)
 * @param root0.searchQuery - Current search query value
 * @param root0.onSearchChange - Search input change handler
 * @param root0.sortLabel - Active row ordering, spelled out
 * @param root0.sortOptions - Ordering menu rows
 * @param root0.isSortOpen - Whether the ordering menu is open
 * @param root0.onSortToggle - Toggles the ordering menu
 * @param root0.onSortClose - Closes the ordering menu
 * @param root0.onCreatePipeline - Close the drawer and open the New Pipeline modal
 * @param root0.onTogglePersonal - Chevron tap on Personal header
 * @param root0.onToggleTeam - Chevron tap on Team header
 * @param root0.onToggleSystem - Chevron tap on System header
 * @param root0.onSelectPipeline - Row tap handler (passes the board id)
 * @param root0.onClose - Dismissal handler (wired to scrim + ESC)
 */
const SeeAllPipelinesDrawerView = ({
  isOpen,
  loading,
  error,
  onRetry,
  personal,
  team,
  system,
  isPersonalCollapsed,
  isTeamCollapsed,
  isSystemCollapsed,
  isEmpty,
  hasUnreachableBoards = false,
  searchQuery,
  onSearchChange,
  sortLabel,
  sortOptions,
  isSortOpen,
  onSortToggle,
  onSortClose,
  onCreatePipeline,
  onTogglePersonal,
  onToggleTeam,
  onToggleSystem,
  onSelectPipeline,
  onClose,
}: SeeAllPipelinesDrawerViewProps) => (
  <Drawer
    isOpen={isOpen}
    onClose={onClose}
    size="sm"
    anchor="right"
    testID={TEST_ID_SEE_ALL_PIPELINES_DRAWER.ROOT}
  >
    <DrawerBackdrop />
    <DrawerContent className="bg-surface-base p-0">
      <DrawerHeader className="h-14 flex-row items-center justify-between gap-3 border-b border-outline-subtle px-6 py-3">
        <Text
          {...({
            "data-testid": TEST_ID_SEE_ALL_PIPELINES_DRAWER.TITLE,
          } as Record<string, string>)}
          variant="title"
          className="flex-1 uppercase text-content-secondary"
        >
          All pipelines
        </Text>
        {/* Same close affordance as the Add Players drawer: 36-tall outline
            secondary at radius 4, so the two drawers dismiss the same way. */}
        <Button
          testID={TEST_ID_SEE_ALL_PIPELINES_DRAWER.CLOSE}
          accessibilityLabel="Close all-pipelines panel"
          tone="secondary"
          variant="outline"
          size="sm"
          icon={X}
          iconOnly
          className="rounded-sm"
          onPress={onClose}
        />
      </DrawerHeader>

      {/* Controls block: search + New on one row, the ordering select beneath.
          New sits beside search rather than in the header because it is an
          action ON this list, not drawer chrome.

          `shrink-0` is load-bearing, not decoration. `DrawerBody` is a
          ScrollView whose base style is `shrink-0`, so once the list is long
          enough to exceed the drawer it refuses to shrink and the column
          overflows. This block was then the only sibling left that COULD
          shrink, so it collapsed and its contents painted over the first
          section header. Pinning it fixes the overlap at the source; the
          matching `flex-1` on DrawerBody below is the other half. */}
      <Stack className="shrink-0 gap-2 px-6 py-4">
        <Stack direction="horizontal" className="items-center gap-2">
          <Box className="flex-1">
            <Input
              variant="outline"
              size="md"
              className="h-9 rounded-sm border-outline-strong data-[focus=true]:border-outline-strong"
              leadingIcon={SearchIcon}
              leadingIconTone="muted"
              field={{
                testID: TEST_ID_SEE_ALL_PIPELINES_DRAWER.SEARCH_INPUT,
                value: searchQuery,
                onChangeText: onSearchChange,
                placeholder: "Search list...",
                className:
                  "web:outline-none pl-2 text-body text-content-secondary",
                returnKeyType: "search",
              }}
            />
          </Box>
          {/* Filled accent — this is the drawer's one primary action, and
              `accent-primary` is the product's blue CTA (SE-6233), which is
              exactly the #007aff the spec asks for. */}
          {onCreatePipeline === undefined ? null : (
            <Button
              testID={TEST_ID_SEE_ALL_PIPELINES_DRAWER.NEW_PIPELINE}
              accessibilityLabel="New pipeline"
              label="New"
              icon={Plus}
              variant="solid"
              tone="accent"
              size="sm"
              className="shrink-0 rounded-sm"
              onPress={onCreatePipeline}
            />
          )}
        </Stack>

        <SelectDropdown
          isOpen={isSortOpen}
          onClose={onSortClose}
          onToggle={onSortToggle}
          triggerContent={renderSortTrigger(sortLabel)}
          triggerTestID={TEST_ID_SEE_ALL_PIPELINES_DRAWER.SORT_TRIGGER}
          triggerAccessibilityLabel="Order pipelines"
          triggerClassName={SORT_TRIGGER_CLASS}
          options={sortOptions}
        />
      </Stack>
      {/* `flex-1` so the list scroller takes the space that is LEFT after the
          header and controls, and scrolls inside it. Without it the body sizes
          to its content and, being `shrink-0` by base style, pushes the column
          past the drawer instead of scrolling — the sibling
          PipelineActivityLogDrawer already carries `flex-1` for this reason. */}
      <DrawerBody className="!my-0 flex-1 px-0 pb-6">
        {loading ? (
          <Stack
            direction="horizontal"
            className="items-center justify-center px-6 py-8"
          >
            <Spinner testID={TEST_ID_SEE_ALL_PIPELINES_DRAWER.LOADING} />
          </Stack>
        ) : error ? (
          <Stack
            testID={TEST_ID_SEE_ALL_PIPELINES_DRAWER.ERROR_STATE}
            className="items-center gap-3 px-6 py-8"
          >
            <Text variant="body" className="text-content-muted">
              Couldn&apos;t load pipelines. Please try again.
            </Text>
            <Button
              testID={TEST_ID_SEE_ALL_PIPELINES_DRAWER.RETRY}
              label="Try again"
              variant="outline"
              tone="secondary"
              size="sm"
              onPress={onRetry}
              accessibilityLabel="Retry loading pipelines"
            />
          </Stack>
        ) : isEmpty ? (
          <Stack>
            {/* SE-6775: the affordance must survive into the EMPTY branch, and
                this is the branch that matters most. Under SE-6577's
                newest-first ordering the loaded window holds the most recently
                created boards, and in the shared org those are SE-6568's leaked
                fixtures — so a truncated walk can return ZERO pipeline boards
                while the org has plenty, and this is what renders then. Without
                it the drawer says "No pipelines found" to a user who has them,
                on the one screen that promises to show all of them. */}
            {hasUnreachableBoards ? renderTruncationIndicator() : null}
            <Box
              testID={TEST_ID_SEE_ALL_PIPELINES_DRAWER.EMPTY_STATE}
              className="items-center px-6 py-8"
            >
              <Text variant="body" className="text-content-muted">
                No pipelines found
              </Text>
            </Box>
          </Stack>
        ) : (
          <Stack>
            {hasUnreachableBoards ? renderTruncationIndicator() : null}
            <SeeAllPipelinesSection
              title="Personal"
              rows={personal}
              isCollapsed={isPersonalCollapsed}
              isDrawerOpen={isOpen}
              onToggle={onTogglePersonal}
              onSelectRow={onSelectPipeline}
              sectionTestId={TEST_ID_SEE_ALL_PIPELINES_DRAWER.SECTION_PERSONAL}
              chevronTestId={TEST_ID_SEE_ALL_PIPELINES_DRAWER.CHEVRON_PERSONAL}
            />
            <SeeAllPipelinesSection
              title="Team"
              rows={team}
              isCollapsed={isTeamCollapsed}
              isDrawerOpen={isOpen}
              onToggle={onToggleTeam}
              onSelectRow={onSelectPipeline}
              sectionTestId={TEST_ID_SEE_ALL_PIPELINES_DRAWER.SECTION_TEAM}
              chevronTestId={TEST_ID_SEE_ALL_PIPELINES_DRAWER.CHEVRON_TEAM}
            />
            <SeeAllPipelinesSection
              title="System"
              rows={system}
              isCollapsed={isSystemCollapsed}
              isDrawerOpen={isOpen}
              onToggle={onToggleSystem}
              onSelectRow={onSelectPipeline}
              sectionTestId={TEST_ID_SEE_ALL_PIPELINES_DRAWER.SECTION_SYSTEM}
              chevronTestId={TEST_ID_SEE_ALL_PIPELINES_DRAWER.CHEVRON_SYSTEM}
            />
          </Stack>
        )}
      </DrawerBody>
    </DrawerContent>
  </Drawer>
);

SeeAllPipelinesDrawerView.displayName = "SeeAllPipelinesDrawerView";

export default memo(SeeAllPipelinesDrawerView);
